import { Channel, ChannelConnectParams, ChannelStatus, ChannelStatusListener } from 'core/models/channel';
import { MediaDevice } from 'core/models/mediaDevice';
import { SoraConnector } from 'infrastructure/connectors/SoraConnector';
import { ConnectionStatusListener } from 'infrastructure/connectors/StatusObservableConnector';
import { ignore } from 'utility/ignore';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { LatencyData } from 'core/models/latency';

type AuthMetadata = { token?: string; expire?: number };
type ErrorResponse = { Message?: string };
type AuthResponse = AuthMetadata | ErrorResponse;

function isError(json: AuthResponse): json is ErrorResponse {
    return Object.prototype.hasOwnProperty.call(json, 'Message');
}

async function getMetadata() {
    // FIXME: cache sora-auth token
    const res = await fetch('/api/sora/token');
    try {
        const metadata = await res.json() as AuthResponse;
        if (isError(metadata)) {
            if (metadata.Message && metadata.Message.match(/not authorized/)) {
                throw new Error('authorization failed');
            } else {
                throw new Error(`unknown response from sora auth server: ${JSON.stringify(metadata)}`);
            }
        }
        return metadata;
    } catch (e) {
        console.error('unexpected response format (no token proxy is running?)');
        return {};
    }
}

export class DummyChannel implements Channel {
    readonly mediaDeviceId: MediaDevice.Id;
    readonly currentStatus: ChannelStatus | undefined;

    private connector: SoraConnector | undefined;

    constructor(camera: MediaDevice) {
        this.mediaDeviceId = camera.id;
    }

    async prepare(): Promise<void> {
        /* */
    }

    async connect(params: ChannelConnectParams): Promise<MediaStream> {
        const metadata = await getMetadata();

        const connector = new SoraConnector({
            signalingServer: 'wss://devwebrtc.tier4.jp/signaling',
            roomName: `${this.mediaDeviceId}`,
            token: metadata.token,
            expire: metadata.expire,
        });

        connector.addStatusListener(this.handleStatus);
        const {stream} = await connector.connect(params);
        return stream;
    }

    async disconnect() {
        if (this.connector) {
            this.connector.removeStatusListener(this.handleStatus);
            this.connector.disconnect();
            this.connector = undefined;
        }
    }

    get isRecordable(): boolean {
        return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async enableRecording(_enabled: boolean) {
        /* */
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        ignore(selector);
        return undefined;
    }

    getLatency(): LatencyData | undefined {
        return undefined;
    }

    private readonly listenerManager = createListenerManager<ChannelStatusListener>();

    private readonly handleStatus: ConnectionStatusListener = status => {
        this.listenerManager.forEach(listener => {
            listener({
                publishing: status.publishing,
            });
        });
    };

    addStatusListener(listener: ChannelStatusListener) {
        this.listenerManager.addListener(listener);
    }

    removeStatusListener(listener: ChannelStatusListener) {
        this.listenerManager.removeListener(listener);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addReconnectListener(_listener: (stream: MediaStream) => void) {
        /* */
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeReconnectListener(_listener: (stream: MediaStream) => void) {
        /* */
    }
}
